import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Wrench, Clock, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Services() {
  const { t, i18n } = useTranslation();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const lang = i18n.language;

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="services-page">
      {/* Header */}
      <section className="relative py-20 overflow-hidden bg-gray-50">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1753964724380-2c5ae02512a8?w=1920"
            alt="Services"
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white via-white/95 to-white" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center">
                <Wrench className="h-6 w-6 text-white" />
              </div>
              <h1 className="text-4xl font-bold text-black">{t('dept.services')}</h1>
            </div>
            <p className="text-gray-600 text-lg">{t('dept.services.desc')}</p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="space-y-4">
                  <Skeleton className="h-48 w-full bg-gray-100" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, index) => (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <Card className="group bg-white border-gray-200 hover:shadow-lg transition-all duration-300 h-full" data-testid={`service-card-${service.id}`}>
                    <CardContent className="p-6 flex flex-col h-full">
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                          <Wrench className="h-6 w-6 text-black" />
                        </div>
                        {service.duration_hours && (
                          <div className="flex items-center gap-1 text-gray-500 text-sm">
                            <Clock className="h-4 w-4" />
                            <span>{service.duration_hours}h</span>
                          </div>
                        )}
                      </div>
                      
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-1 font-mono">
                        {service.category}
                      </p>
                      
                      <h3 className="text-xl font-semibold text-black mb-2">
                        {lang === 'fr' ? service.name_fr : service.name_en}
                      </h3>
                      
                      <p className="text-gray-500 text-sm mb-4 flex-1">
                        {lang === 'fr' ? service.description_fr : service.description_en}
                      </p>
                      
                      <div className="flex items-end justify-between mt-auto pt-4 border-t border-gray-200">
                        <div>
                          <p className="text-2xl font-bold text-black font-mono">
                            {formatPrice(service.price)}
                          </p>
                          {service.duration_hours && (
                            <p className="text-gray-400 text-xs">par intervention</p>
                          )}
                        </div>
                        <Link to={`/services/book/${service.id}`}>
                          <Button className="bg-black text-white hover:bg-gray-800" data-testid={`book-service-${service.id}`}>
                            Réserver
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-black border-t border-gray-200">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Besoin d'un service personnalisé?</h2>
          <p className="text-gray-400 mb-6 max-w-xl mx-auto">
            Contactez-nous pour discuter de vos besoins spécifiques et obtenir un devis personnalisé.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-black hover:bg-gray-200">
              Demander un devis
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
